import { withTransaction } from '@elastic/apm-rum-react'
import { Button, Media, Modal, Steps } from '@hub/shared'
import MediaKit, { Profile } from '@hub/shared/interfaces/mediakit.interface'
import BrandSelect from '@src/components/brand-select/brand-select.component'
import CardBrand from '@src/components/card-brand/card-brand.component'
import FeedSelector from '@src/components/feed-selector/feed-selector.component'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './brands.page.scoped.scss'
import './brands.page.scss'

interface Props {
  mediakit: MediaKit
  idProfile: string | number
  onChange: (mediakit: MediaKit) => void
  getValidation: (validated: boolean) => void
}

const BrandPageComponent = ({ mediakit, idProfile, onChange, getValidation }: Props) => {
  const [loading, setLoading] = useState<boolean | undefined>(undefined)
  const [photo, setPhoto] = useState<Media | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [openModalLinkBrand, setOpenModalLinkBrand] = useState<boolean | undefined>(undefined)
  const [stepActive, setStepActive] = useState<number>(0)
  const [profileIndexSelected, setProfileIndexSelected] = useState<string | undefined>(undefined)

  const { t } = useTranslation('brandsPage')

  const timestamp = `random-id-${(1 + Date.now() + Math.random()).toString().replace('.', '')}`

  useEffect(() => {
    getValidation(true)
  }, [mediakit?.whoAnnounced?.brands, getValidation])

  const onCloseModal = () => {
    setLoading(false)
    setPhoto(null)
    setProfile(null)
    setOpenModalLinkBrand(false)
    setStepActive(0)
    setProfileIndexSelected(undefined)
  }

  const sliderConfig = useMemo(
    () => ({
      0: { title: t('selectPhoto'), subtitle: t('brandCampaignRef') },
      1: { title: t('linkBrand'), subtitle: '' },
    }),
    [t]
  )

  const handleClickPrev = () => {
    if (stepActive === 0) {
      onCloseModal()
    } else {
      setStepActive(stepActive - 1)
    }
  }

  const handleClickNext = () => {
    if (stepActive === 1) {
      handleChangeProfile()
      onCloseModal()
    } else {
      setStepActive(stepActive + 1)
    }
  }

  const handleChangeProfile = () => {
    if (photo && profile) {
      if (profileIndexSelected) {
        const profileUpdatedIndex = mediakit?.whoAnnounced?.brands?.findIndex((brand) => brand.profile.id === profileIndexSelected)
        if (profileUpdatedIndex === 0 || (profileUpdatedIndex && profileUpdatedIndex > -1)) {
          mediakit?.whoAnnounced?.brands?.splice(profileUpdatedIndex, 1, {
            image: photo,
            profile: profile,
          })
        }
        setProfileIndexSelected(undefined)
      } else {
        onChange({
          ...mediakit,
          whoAnnounced: {
            ...mediakit?.whoAnnounced,
            brands: mediakit?.whoAnnounced?.brands
              ? [{ image: photo, profile: profile }, ...mediakit?.whoAnnounced?.brands]
              : [{ image: photo, profile: profile }],
          },
        })
      }
    }
  }

  const footer = () => {
    return (
      <div className="actions-footer">
        <Button
          size="sm"
          inverted
          color="transparent"
          borderColor="transparent"
          textColor="var(--gray_dark)"
          style={{ padding: '0.25rem 1rem' }}
          onClick={() => handleClickPrev()}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('goBack')}</span>
        </Button>

        <Button
          width={''}
          style={{ padding: '0.25rem 1rem' }}
          disabled={(stepActive === 0 && !photo) || (stepActive === 1 && !profile)}
          onClick={() => handleClickNext()}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{t('next')}</span>
        </Button>
      </div>
    )
  }

  const header = () => (
    <div className="brand-header">
      <div className="logo-instagram">
        <i className="fa-brands fa-instagram"></i>
      </div>
      <div className="brand-header-content">
        <div className="brand-header-title">{sliderConfig[stepActive as keyof typeof sliderConfig]?.title}</div>
        <div className="brand-header-subtitle">{sliderConfig[stepActive as keyof typeof sliderConfig]?.subtitle}</div>
      </div>
    </div>
  )

  return (
    <div className="brands-page">
      <h5 className="m-0">{t('linkPostBrand')}</h5>
      <p>{t('addPhotosAndLink')}</p>
      <div className="brand-list">
        <CardBrand onOpenModalLinkBrand={(openModalLinkBrand: boolean) => setOpenModalLinkBrand(openModalLinkBrand)} brand={undefined} />
        {mediakit?.whoAnnounced?.brands?.map((brand, index) => (
          <CardBrand
            key={index}
            brand={brand}
            idProfile={idProfile}
            mediakit={mediakit}
            onChangeMediaKit={onChange}
            onOpenModalLinkBrand={(openModalLinkBrand: boolean) => setOpenModalLinkBrand(openModalLinkBrand)}
            onProfileIndexSelected={(profileIndexSelected?: string) => setProfileIndexSelected(profileIndexSelected)}
          />
        ))}
        <Modal
          open={openModalLinkBrand}
          onCloseChange={onCloseModal}
          header={header()}
          footer={footer()}
          forceMobileNoMargin
          style={{ position: 'absolute', bottom: 0 }}
        >
          <div className="brands-linked-steps">
            <Steps steps={[{ status: 'one' }, { status: 'two' }]} active={stepActive} />
          </div>
          <div className="brand-modal-content">
            {stepActive === 0 && (
              <div className="content-brand">
                {openModalLinkBrand ? (
                  <FeedSelector
                    id={`feed-selector-${timestamp}`}
                    getOnLoading={(loading: boolean) => setLoading(loading)}
                    onSelectPhoto={(photo: Media) => setPhoto(photo)}
                    socialNetwork="instagram"
                    name={`feed-selector-${timestamp}`}
                    idProfile={idProfile}
                  />
                ) : null}
              </div>
            )}
            {stepActive === 1 && <BrandSelect onSelectProfile={(profile: Profile) => setProfile(profile)} />}
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default withTransaction('BrandPageComponent', 'component')(BrandPageComponent)
